import addDays from 'date-fns/addDays'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import zonedTimeToUtc from 'date-fns-tz/zonedTimeToUtc'
import { formatToTimeZone, parseFromTimeZone } from 'date-fns-timezone'

import { getUnixTime } from 'date-fns'

import { range } from '~/utils/helpers'

export function getUnixTimeInSeconds(): number {
  return getUnixTime(new Date())
}

function parseDate(isoDate: string, timeZone?: string): string {
  if (!timeZone) return isoDate
  return parseFromTimeZone(isoDate, { timeZone }).toISOString()
}

export function parseDatetime(isoDate: string, hours: number, minutes: number, timezone?: string): string {
  return parseDate(`${isoDate} ${getTimeLabel(hours)}:${getTimeLabel(minutes)}`, timezone)
}

export function getTimeLabel(value: number): string {
  return String(value).padStart(2, '0')
}

export function getTimezoneOffset(timezone?: string): string {
  return timezone ? formatToTimeZone(new Date().toISOString(), '[GMT]Z (z)', { timeZone: timezone }) : ''
}

export function formatMonthNameDay(date: string): string {
  return format(parseISO(date), 'MMMM d')
}

export function formatShortMonthNameDay(date: Date, timezone?: string): string {
  if (!timezone) return format(date, 'd MMM')
  return formatToTimeZone(date.toISOString(), 'D MMM', { timeZone: timezone })
}

export function formatInterval(starTime: Date, endTime: Date, timezone?: string): string {
  const timeInterval = `${formatAstime(starTime, timezone)} - ${formatAstime(endTime, timezone)}`

  if (!timezone) return timeInterval

  return `${timeInterval} - ${getTimezoneOffset(timezone)}`
}

function formatAstime(date: Date, timezone?: string): string {
  if (!timezone) return format(date, 'HH:mm')
  return formatToTimeZone(date, 'HH:mm', { timeZone: timezone })
}

export function formatAsDay(date: Date, timezone?: string): string {
  if (!timezone) return format(date, 'yyyy-MM-dd')
  return formatToTimeZone(date, 'YYYY-MM-DD', { timeZone: timezone })
}

export function formatAsDayWithTimezone(date: Date, timezone?: string): string {
  if (!timezone) return format(date, 'yyyy-MM-dd')
  return formatToTimeZone(date, 'YYYY-MM-DD [GMT]Z (z)', { timeZone: timezone })
}

export function formatAsDateTimeWithTimezone(date: Date, timezone?: string): string {
  if (!timezone) return format(date, 'yyyy-MM-dd HH:mm')
  return formatToTimeZone(date, 'YYYY-MM-DD HH:mm [GMT]Z (z)', { timeZone: timezone })
}

export function createTimeWithUserTimezone(date: Date, timezone?: string): string {
  if (!timezone) {
    return date.toISOString()
  }

  const zonedTime = zonedTimeToUtc(date, timezone)
  return zonedTime.toISOString()
}

export function getFirstDateOfYear(year: number): Date {
  return new Date(year, 0, 1)
}

export function getFirstDateOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

export function generateUtcIsoString(date: Date): string {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString()
}

/**
 * Get the week number for a given date.
 * @param date
 * @returns {number} Week number.
 */
export function getWeekNumberFromDate(date: Date): number {
  date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7))

  const dateValue = date.valueOf()
  const dateFirstValue = getFirstDateOfYear(date.getUTCFullYear()).valueOf()

  return Math.ceil(((dateValue - dateFirstValue) / 86400000 + 1) / 7)
}

/**
 * Get the number of weeks in a given year.
 * See {@link https://www.timeanddate.com/date/week-numbers.html}.
 * @param year
 * @returns {number} Number of weeks in the year.
 */
export function getWeeksInYear(year: number): number {
  const leapYear = isLeapYear(year)
  const firstDateOfYear = getFirstDateOfYear(year)

  if (firstDateOfYear.getDay() === 4 || (leapYear && firstDateOfYear.getDay() === 3)) {
    return 53
  } else {
    return 52
  }
}

/**
 * Get the very first Monday of the year.
 * @param date
 * @returns {Date} Date of the Monday.
 */
function findFirstMonday(date: Date): Date {
  if (date.getDay() === 1) {
    return date
  } else {
    return findFirstMonday(new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1))
  }
}

/**
 * Get the date of the first Monday of the year.
 * @param year
 * @returns {Date} Date of the first Monday of the year.
 */
function getMondayOfFirstWeekOfYear(year: number): Date {
  const firstDateOfYear = getFirstDateOfYear(year)
  const date = findFirstMonday(firstDateOfYear)
  const weekNumber = getWeekNumberFromDate(date)

  if (weekNumber === 1) {
    return date
  } else {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 7)
  }
}

/**
 * Get the first date of a given week.
 * @param date
 * @param weekNumber
 * @returns {Date} Date of the first day of the week.
 */
export function getFirstDateOfWeek(weekNumber: number, year: number): Date {
  const weeksInYear = getWeeksInYear(year)

  if (weekNumber < 1 || weekNumber > weeksInYear) {
    throw new Error(`Week number is out of range, expected 1-${weeksInYear}, got ${weekNumber}`)
  }

  const mondayOfFirstWeek = getMondayOfFirstWeekOfYear(year)
  const totalDays = (weekNumber - 1) * 7
  return addDays(mondayOfFirstWeek, totalDays)
}

/**
 * Get the dates of the week for a given week number and year. Starts on Monday.
 * @param weekNumber
 * @param year
 * @returns {Date[]} List of dates.
 */
export function getDatesForWeek(weekNumber: number, year: number): Date[] {
  const date = getFirstDateOfWeek(weekNumber, year)

  return range(7).map((idx) => {
    const y = date.getFullYear()
    const m = date.getMonth()
    const d = date.getDate() + idx
    return new Date(y, m, d)
  })
}
